import { useCallback, useState } from 'react'
import ReactDOM from 'react-dom'

import Button from '../../../common/Button'

const ENGAGE_MESSAGE =
    'Engaging Do Not Lien will stop ALL lien processes for this case (Notice of Intent, Original, Amended, Satisfaction, and manual lien creation). Continue?'

const DISENGAGE_MESSAGE =
    'Disengaging Do Not Lien will resume automated lien processing for this case on the next run. Continue?'

const overlayStyle = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1002,
}

const cardStyle = {
    backgroundColor: 'var(--background, var(--white, #fff))',
    borderRadius: 8,
    padding: '16px 20px',
    minWidth: 320,
    maxWidth: 'min(420px, 92vw)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.25)',
}

const titleStyle = { margin: '0 0 8px', fontSize: 16, fontWeight: 600 }
const textStyle = { margin: '0 0 16px', fontSize: 14, lineHeight: 1.45, color: 'var(--subtitle, #555)' }
const actionsStyle = { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }

export function DoNotLienConfirmModal({ engaging, onConfirm, onCancel }) {
    const overlayRoot = typeof document !== 'undefined' ? document.getElementById('overlay-root') : null
    if (!overlayRoot) {
        return null
    }

    const message = engaging ? ENGAGE_MESSAGE : DISENGAGE_MESSAGE
    const title = engaging ? 'Engage Do Not Lien?' : 'Disengage Do Not Lien?'

    return ReactDOM.createPortal(
        <div style={overlayStyle} role="presentation" onClick={onCancel}>
            <div
                style={cardStyle}
                role="dialog"
                aria-modal="true"
                aria-labelledby="do-not-lien-confirm-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 id="do-not-lien-confirm-title" style={titleStyle}>{title}</h3>
                <p style={textStyle}>{message}</p>
                <div style={actionsStyle}>
                    <Button click={onCancel} btnStyle="btn_white">Cancel</Button>
                    <Button click={onConfirm} color={engaging ? 'btn_danger' : undefined}>Confirm</Button>
                </div>
            </div>
        </div>,
        overlayRoot,
    )
}

/**
 * React hook that renders the Do Not Lien confirmation modal and exposes an
 * imperative `confirm({ engaging })` function returning a Promise<boolean>.
 *
 * Usage:
 *   const { confirm, modal } = useDoNotLienConfirm()
 *   const ok = await confirm({ engaging: true })
 *   return <>{modal}{...}</>
 */
export function useDoNotLienConfirm() {
    const [pending, setPending] = useState(null)

    const confirm = useCallback(({ engaging }) => {
        return new Promise((resolve) => {
            setPending({ engaging: Boolean(engaging), resolve })
        })
    }, [])

    const finish = useCallback((result) => {
        setPending((current) => {
            if (current && typeof current.resolve === 'function') {
                current.resolve(result)
            }
            return null
        })
    }, [])

    const modal = pending ? (
        <DoNotLienConfirmModal
            engaging={pending.engaging}
            onConfirm={() => finish(true)}
            onCancel={() => finish(false)}
        />
    ) : null

    return { confirm, modal }
}
